import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingModal = ({ open, onOpenChange }) => {
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    treatment: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Prepare booking data
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        treatment: formData.treatment,
        preferredDate: date ? format(date, 'PPP') : null,
        message: formData.message || null
      };
      
      // Submit to backend API
      const response = await axios.post(`${API}/bookings`, bookingData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success(response.data.message || 'Booking request submitted successfully!');
        
        setTimeout(() => {
          setIsSubmitted(false);
          onOpenChange(false);
          setFormData({ name: '', email: '', phone: '', treatment: '', message: '' });
          setDate(undefined);
        }, 2000);
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-black">Book Your Appointment</DialogTitle>
          <DialogDescription className="text-gray-600">
            Fill in your details and we'll contact you to confirm your appointment.
          </DialogDescription>
        </DialogHeader>
        
        {isSubmitted ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-black mb-2">Request Submitted!</h3>
            <p className="text-gray-600 text-center">We'll be in touch shortly to confirm your appointment.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name" className="text-black">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                className="border-gray-300 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
              />
            </div>
            
            <div>
              <Label htmlFor="email" className="text-black">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                className="border-gray-300 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
              />
            </div>
            
            <div>
              <Label htmlFor="phone" className="text-black">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
                className="border-gray-300 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
              />
            </div>
            
            <div>
              <Label htmlFor="treatment" className="text-black">Treatment *</Label>
              <Select value={formData.treatment} onValueChange={(value) => handleChange('treatment', value)} required>
                <SelectTrigger className="border-gray-300 focus:border-[#D4AF37] focus:ring-[#D4AF37]">
                  <SelectValue placeholder="Select a treatment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dermal-fillers">Dermal Fillers</SelectItem>
                  <SelectItem value="anti-wrinkle">Anti-Wrinkle Injections</SelectItem>
                  <SelectItem value="lip-augmentation">Lip Augmentation</SelectItem>
                  <SelectItem value="skin-rejuvenation">Skin Rejuvenation</SelectItem>
                  <SelectItem value="facial-contouring">Facial Contouring</SelectItem>
                  <SelectItem value="jawline">Jawline Enhancement</SelectItem>
                  <SelectItem value="consultation">Free Consultation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-black">Preferred Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal border-gray-300 hover:border-[#D4AF37]"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, 'PPP') : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div>
              <Label htmlFor="message" className="text-black">Additional Information</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                rows={3}
                className="border-gray-300 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                placeholder="Any specific requests or concerns?"
              />
            </div>
            
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-white font-semibold py-3 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;